import React, { useState, useEffect } from "react";
import Joi from "joi-browser";
import ReCAPTCHA from "react-google-recaptcha";
import useForm from "../../app/hooks/useForm";
import useControls from "../../app/hooks/useControls";
import {
  useAuthStartMutation,
  useLazyGetUserTypeQuery,
} from "../../app/store/authApiSlice";

const LoginStep1 = ({ handleStep1, userError, type }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const [authStart] = useAuthStartMutation();
  const [getUserType] = useLazyGetUserTypeQuery();

  const schema = {
    validId: Joi.string().required().label("Valid ID"),
    ReCAPTCHA: Joi.string().required().label("Re-Captcha"),
  };

  const onCaptchaChange = (value) => {
    setStateData((prev) => ({
      ...prev,
      ReCAPTCHA: value || "",
    }));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const recaptchaResponse = document.getElementById("g-recaptcha-response");
      if (recaptchaResponse) {
        recaptchaResponse.setAttribute("aria-label", "Captcha verification");
        recaptchaResponse.setAttribute("tabindex", "-1");
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const doSubmit = async () => {
    setIsProcessing(true);
    try {
      // 1. Validate ValidId and obtain ChallengeId from the new Auth start API
      const { data: startResult, error: startError } = await authStart({
        validId: stateData["validId"],
        portalType: "customer",
      });

      if (startError) {
        const res = startError;
        let isInactive =
          res?.data === "inactive" || res?.data?.message === "inactive";

        // Inspect legacy endpoint to determine if failure was due to account deactivation
        if (!isInactive) {
          const { error: typeErr } = await getUserType(stateData["validId"]);
          if (typeErr?.data === "inactive") {
            isInactive = true;
          }
        }

        const errorMessage = isInactive
          ? "Your account has been deactivated, please contact system administrator systemadmin@workfinity.com"
          : "Invalid Id";

        setStateErrors((prev) => ({
          ...prev,
          validId: errorMessage,
        }));
        return;
      }

      localStorage.setItem("challengeId", startResult.challengeId);

      let userType = "applicant";
      // 2. Query GetUserType to resolve layout-level user types (recruiter, candidate, subcontractor)
      const { data: userTypeResult, error: userTypeError } = await getUserType(
        stateData["validId"]
      );
      if (!userTypeError && userTypeResult) {
        userType = userTypeResult;
      } else {
        // Fallback default
        userType = "applicant";
      }

      localStorage.setItem("userType", userType);
      handleStep1({ ...stateData, userType });
    } finally {
      setIsProcessing(false);
    }
  };

  const {
    stateData,
    setStateData,
    stateErrors,
    setStateErrors,
    handleChange,
    validate,
    handleSubmit,
  } = useForm(schema, doSubmit);

  useEffect(() => {
    if (userError !== undefined) {
      setStateErrors((prev) => ({
        ...prev,
        validId: userError,
      }));
    }
  }, [userError, setStateErrors]);

  const { renderInput } = useControls(
    stateData,
    stateErrors,
    handleChange,
    schema
  );

  return (
    <React.Fragment>
      <div className="form-group">
        <label htmlFor="validId" className="form-control-label">
          Enter Your Valid ID <span style={{ color: "#e63946" }}>*</span>
        </label>
        <input
          type="text"
          className="form-control"
          name="validId"
          id="validId"
          placeholder="Enter Your Valid ID"
          value={stateData["validId"] || ""}
          onChange={handleChange}
        />
        {stateErrors["validId"] && (
          <div className="alert alert-danger mt-2 mb-0">{stateErrors["validId"]}</div>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "center", margin: "8px 0 12px 0" }}>
        <ReCAPTCHA
          sitekey="6Le08xsUAAAAAKC6Oglhe-haRVGzA8KTLUZ-jmEK"
          onChange={onCaptchaChange}
        />
      </div>
      <button
        type="submit"
        className="btn w-100 wf-login-btn-primary"
        disabled={Boolean(validate()) || !stateData.ReCAPTCHA || isProcessing}
        onClick={handleSubmit}
      >
        {isProcessing ? (
          <React.Fragment>
            <i className="fa fa-refresh fa-spin mr-2" aria-hidden="true"></i>
            Verifying ID...
          </React.Fragment>
        ) : (
          <React.Fragment>
            <i className="fa fa-arrow-right mr-2" aria-hidden="true"></i>
            Next
          </React.Fragment>
        )}
      </button>
    </React.Fragment>
  );
};

export default LoginStep1;
